/**
 * TenantPrivacySection — iter85 (CCPA / CAN-SPAM, Site Admin scope)
 *
 * Tenant admins collect privacy requests from their users and push them
 * into the platform queue. Platform Admin reviews + executes; status is
 * visible here (own-tenant requests only).
 */
import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldAlert, Loader2, Plus, XCircle } from "lucide-react";

const STATUS_COLOR = {
    pending: "text-amber-400 border-amber-400/40",
    completed: "text-emerald-400 border-emerald-400/40",
    cancelled: "text-zinc-500 border-[#333]",
};

export default function TenantPrivacySection() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ email: "", request_type: "anonymize", notes: "" });
    const [submitting, setSubmitting] = useState(false);

    const load = useCallback(async () => {
        try {
            const r = await api.privacyRequestsList();
            setRows(r.rows);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load privacy requests");
        }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const submit = async () => {
        const email = form.email.trim().toLowerCase();
        if (!email.includes("@")) { toast.error("Enter a valid email"); return; }
        setSubmitting(true);
        try {
            await api.privacyRequestSubmit({ email, request_type: form.request_type, notes: form.notes });
            toast.success("Request pushed to the Platform queue");
            setForm({ email: "", request_type: "anonymize", notes: "" });
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to submit request");
        }
        setSubmitting(false);
    };

    const cancel = async (rid) => {
        try {
            await api.privacyRequestCancel(rid);
            toast.success("Request cancelled");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Cancel failed");
        }
    };

    return (
        <section className="brutal-card" data-testid="tenant-privacy-section">
            <div className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                    <ShieldAlert className="w-5 h-5 text-cyber shrink-0" />
                    <div>
                        <h3 className="font-display font-extrabold text-base uppercase">Privacy Requests (CCPA / CAN-SPAM)</h3>
                        <p className="text-[11px] text-zinc-500">
                            Received a data-deletion or do-not-email request? Push it to the Platform queue — a Platform
                            Admin executes it (identity anonymized, game history preserved, email suppressed forever).
                        </p>
                    </div>
                </div>

                <div className="brutal-card-light p-3 space-y-2" data-testid="tenant-privacy-form">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <input
                            data-testid="tenant-privacy-email"
                            type="email"
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            placeholder="user@example.com"
                            className="brutal-input text-xs"
                        />
                        <select
                            data-testid="tenant-privacy-type"
                            value={form.request_type}
                            onChange={(e) => setForm({ ...form, request_type: e.target.value })}
                            className="brutal-input text-xs"
                        >
                            <option value="anonymize">Anonymize (keep game data)</option>
                            <option value="hard_delete">Hard delete (+ remove messages)</option>
                        </select>
                        <input
                            data-testid="tenant-privacy-notes"
                            value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            placeholder="Notes (who asked, when…)"
                            className="brutal-input text-xs"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={submitting}
                        data-testid="tenant-privacy-submit"
                        className="brutal-btn-primary text-xs py-2 disabled:opacity-60"
                    >
                        {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                        {submitting ? "Submitting…" : "Push to Platform queue"}
                    </button>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center gap-2 p-4 text-zinc-400 text-xs">
                        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                    </div>
                ) : rows.length === 0 ? (
                    <p className="text-xs text-zinc-500 italic text-center p-3" data-testid="tenant-privacy-empty">
                        No privacy requests submitted from your tenant yet.
                    </p>
                ) : (
                    <div className="space-y-1.5" data-testid="tenant-privacy-list">
                        {rows.map((row) => (
                            <div key={row.id} className="brutal-card-light p-2.5 flex flex-wrap items-center gap-2" data-testid={`tenant-privacy-row-${row.id}`}>
                                <span className={`pill text-[10px] font-bold uppercase ${STATUS_COLOR[row.status] || "text-zinc-400 border-[#333]"}`}>
                                    {row.status}
                                </span>
                                <span className="font-mono text-xs text-white">{row.email}</span>
                                <span className="pill text-[10px] uppercase border-[#333] text-zinc-400">{row.request_type}</span>
                                <span className="text-[10px] text-zinc-500 ml-auto">
                                    {(row.created_at || "").slice(0, 16).replace("T", " ")}
                                </span>
                                {row.status === "pending" && (
                                    <button
                                        type="button"
                                        onClick={() => cancel(row.id)}
                                        data-testid={`tenant-privacy-cancel-${row.id}`}
                                        className="brutal-btn-ghost text-[10px] py-1 text-red-400"
                                    >
                                        <XCircle className="w-3 h-3" /> Cancel
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}
